// Client-side IP+country lookup with multiple fallbacks so we always get a country.
export type GeoInfo = { ip: string | null; country_code: string | null; country_name: string | null; city: string | null };

let cached: GeoInfo | null = null;
let inflight: Promise<GeoInfo> | null = null;

// Silent fetch: never logs, never throws. Returns null on any failure (incl. 429, CORS, network).
async function silentFetch(url: string): Promise<any | null> {
  try {
    const r = await fetch(url, { cache: "no-store" }).catch(() => null);
    if (!r || !r.ok) return null;
    return await r.json().catch(() => null);
  } catch {
    return null;
  }
}

async function tryGeojs(): Promise<GeoInfo | null> {
  const j = await silentFetch("https://get.geojs.io/v1/ip/geo.json");
  if (!j?.country_code) return null;
  return {
    ip: j.ip ?? null,
    country_code: j.country_code ?? null,
    country_name: j.country ?? null,
    city: j.city ?? null,
  };
}

async function tryIpwho(): Promise<GeoInfo | null> {
  const j = await silentFetch("https://ipwho.is/");
  if (!j?.success || !j?.country_code) return null;
  return {
    ip: j.ip ?? null,
    country_code: j.country_code ?? null,
    country_name: j.country ?? null,
    city: j.city ?? null,
  };
}

export async function getGeo(): Promise<GeoInfo> {
  if (cached) return cached;
  if (inflight) return inflight;
  inflight = (async () => {
    const result =
      (await tryGeojs()) ||
      (await tryIpwho()) ||
      { ip: null, country_code: null, country_name: "Unknown", city: null };
    cached = result;
    return result;
  })();
  return inflight;
}

// ISO country code -> emoji flag
export function flagEmoji(code?: string | null): string {
  if (!code || code.length !== 2) return "🏳️";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 - 65 + c.charCodeAt(0)));
}
